import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FocusedJourneyLayout, MaskContext, MaskTextarea, PrimaryAction, SecondaryAction, MaskClose,
} from '@/components/mask/MaskSystem';
import { dateLocale } from '@/lib/dateLocale';

/* Phase 3 — Eine empfangene Carma bewusst erleben (Focused Journey innerhalb FÜR DICH) */

const IMPULSE_Q = [false, false, false, true, true, true, true];

export const CarmaExperience = ({ carma, isDiscovery, onOpen, onKeep, onUnkeep, onSaveNote, onClose }) => {
  const { t, i18n } = useTranslation();
  const [settled, setSettled] = useState(!isDiscovery);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState(carma.note || '');
  const [saving, setSaving] = useState(false);
  const [keeping, setKeeping] = useState(false);
  const endRef = useRef(null);
  const startRef = useRef(null);
  const scrolledRef = useRef(false);

  const impulse = useMemo(() => {
    const impulses = t('v2.experience.impulses', { returnObjects: true });
    let h = 0;
    for (const c of carma.id) h = (h + c.charCodeAt(0)) % impulses.length;
    return { text: impulses[h], q: IMPULSE_Q[h] };
  }, [carma.id, t]);

  useEffect(() => {
    if (isDiscovery) onOpen(carma.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Adaptive Einblendung: Textlänge, Lesezeit, Scroll ans Ende
  useEffect(() => {
    if (!isDiscovery || settled) return undefined;
    startRef.current = Date.now();
    const secs = Math.min(20, Math.max(4, Math.round(carma.message.length / 16)));
    const timer = setTimeout(() => setSettled(true), secs * 1000);

    const scrollEl = document.querySelector('[data-testid="focused-journey"]');
    const onScroll = () => { scrolledRef.current = true; };
    scrollEl?.addEventListener('scroll', onScroll, { passive: true });

    let observer;
    if (endRef.current) {
      observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && scrolledRef.current && Date.now() - startRef.current >= 4000) {
          setSettled(true);
        }
      }, { root: scrollEl, threshold: 0.9 });
      observer.observe(endRef.current);
    }
    return () => { clearTimeout(timer); scrollEl?.removeEventListener('scroll', onScroll); observer?.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);

  const saveNote = async () => {
    if (saving) return;
    setSaving(true);
    try { await onSaveNote(carma.id, noteText.trim()); setNoteOpen(false); }
    finally { setSaving(false); }
  };

  const keep = async () => {
    if (keeping) return;
    setKeeping(true);
    try { await onKeep(carma.id); } finally { setKeeping(false); }
  };

  const bgWord = isDiscovery ? t('v2.bgWords.fuerDich') : carma.keptAt ? t('v2.bgWords.bewahrt') : t('v2.bgWords.angekommen');
  const dateStr = new Date(carma.createdAt).toLocaleDateString(dateLocale(i18n.language), { day: 'numeric', month: 'long', year: 'numeric' });
  const showNoteLink = (isDiscovery && impulse.q) || !isDiscovery;

  return (
    <FocusedJourneyLayout>
      <MaskClose onClick={onClose} data-testid="experience-close" />
      <div className="relative min-h-screen flex flex-col" data-testid="carma-experience">
        <div className="mask-bg-word mask-bg-top" aria-hidden="true">
          <span style={{ transform: 'translateX(-8%)' }}>{bgWord}</span>
        </div>
        <div className="relative z-10 flex-1 flex flex-col justify-center w-full max-w-xl mx-auto px-6 sm:px-8 py-24">
          <MaskContext>{t('v2.common.from', { name: carma.senderName })} · {dateStr}</MaskContext>
          <p className="mask-enter mask-enter-delay-1 mask-letter text-xl sm:text-2xl text-[var(--text)]" data-testid="experience-message">
            {carma.message}
          </p>
          {carma.context && <p className="mask-enter mask-enter-delay-2 text-sm text-[var(--muted)] italic mt-6">{carma.context}</p>}
          {isDiscovery && !settled && (
            <p className="mask-enter text-sm text-[var(--muted)] mt-10" style={{ animationDelay: '2600ms', animationFillMode: 'backwards' }} data-testid="experience-while-reading">
              {t('v2.experience.whileReading')}
            </p>
          )}
          <div ref={endRef} aria-hidden="true" />

          <div className="mt-14 min-h-[120px]">
            {settled && !noteOpen && (
              <div className="mask-enter space-y-8">
                {isDiscovery && (
                  <p className="text-base text-[var(--muted)] leading-relaxed" data-testid="experience-impulse">{impulse.text}</p>
                )}

                {carma.note && (
                  <div data-testid="experience-note-display">
                    <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-2">{t('v2.experience.yourThoughts')}</p>
                    <p className="mask-letter text-base text-[var(--text)]/85">{carma.note}</p>
                    <button
                      type="button"
                      onClick={() => { setNoteText(carma.note); setNoteOpen(true); }}
                      data-testid="experience-note-edit"
                      className="mt-2 text-sm text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors"
                    >
                      {t('v2.common.edit')}
                    </button>
                  </div>
                )}

                {showNoteLink && !carma.note && (
                  <button
                    type="button"
                    onClick={() => setNoteOpen(true)}
                    data-testid="experience-note-link"
                    className="block text-sm text-[var(--muted)] hover:text-[var(--text)] underline underline-offset-4 decoration-[var(--muted)]/40 transition-colors"
                  >
                    {t('v2.experience.noteLink')}
                  </button>
                )}

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2">
                  {carma.keptAt ? (
                    <>
                      <span className="text-sm text-[var(--text)]/80" data-testid="experience-kept-label">{t('v2.experience.keptLabel')}</span>
                      <SecondaryAction onClick={() => onUnkeep(carma.id)} data-testid="experience-unkeep-btn">{t('v2.experience.unkeep')}</SecondaryAction>
                    </>
                  ) : (
                    <PrimaryAction onClick={keep} disabled={keeping} data-testid="experience-keep-btn">
                      {keeping ? t('v2.common.loading') : t('v2.experience.keep')}
                    </PrimaryAction>
                  )}
                  <SecondaryAction onClick={onClose} data-testid="experience-back-btn">{t('v2.experience.back')}</SecondaryAction>
                </div>
              </div>
            )}

            {settled && noteOpen && (
              <div className="mask-enter">
                <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] mb-4">{t('v2.tagebuch.private')}</p>
                <MaskTextarea
                  autoFocus
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value.slice(0, 1000))}
                  placeholder={t('v2.experience.notePlaceholder')}
                  style={{ minHeight: '22vh' }}
                  data-testid="experience-note-textarea"
                />
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                  <PrimaryAction onClick={saveNote} disabled={saving} data-testid="experience-note-save">
                    {saving ? t('v2.common.loading') : t('v2.tagebuch.hold')}
                  </PrimaryAction>
                  <SecondaryAction onClick={() => { setNoteOpen(false); setNoteText(carma.note || ''); }} data-testid="experience-note-cancel">{t('v2.common.notNow')}</SecondaryAction>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </FocusedJourneyLayout>
  );
};
